import {
  createDarkTheme,
  createLightTheme,
  teamsHighContrastTheme
} from "@fluentui/react-components";
import {
  accent,
  atelier,
  brandRamp,
  chartSeries,
  logoOnDark,
  neutral,
  night,
  statusPairs
} from "./brand.js";
const BRAND_TOKEN_NAMES = [
  "bfLogoTile",
  "bfLogoArch",
  "bfLogoFlow",
  "bfWordBati",
  "bfWordFlow",
  "bfFlowLine",
  "bfSelectedBackground",
  "bfSelectedBackgroundHover",
  "bfSelectedForeground",
  "bfTrack",
  "bfAccentBackground",
  "bfChartSeries1",
  "bfChartSeries2",
  "bfStatusSuccessForeground",
  "bfStatusSuccessBackground",
  "bfStatusWarningForeground",
  "bfStatusWarningBackground",
  "bfStatusDangerForeground",
  "bfStatusDangerBackground",
  "bfStatusInfoForeground",
  "bfStatusInfoBackground",
  "bfStatusNeutralForeground",
  "bfStatusNeutralBackground"
];
const brandTokens = Object.fromEntries(
  BRAND_TOKEN_NAMES.map((name) => [name, `var(--${name})`])
);
const LATIN_FONT_STACK = '"IBM Plex Sans", "Noto Sans", "Segoe UI", system-ui, sans-serif';
const ARABIC_FONT_STACK = `"IBM Plex Sans Arabic", "Noto Sans Arabic", ${LATIN_FONT_STACK}`;
const MONO_FONT_STACK = '"IBM Plex Mono", Consolas, "Courier New", monospace';
const fontStackFor = (dir) => dir === "rtl" ? ARABIC_FONT_STACK : LATIN_FONT_STACK;
const hex = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0").toUpperCase();
function greyLevel(value) {
  const m = /^#([0-9a-f]{2})\1\1$/i.exec(value);
  return m?.[1] ? parseInt(m[1], 16) : null;
}
const NEUTRAL_SURFACE = /^color(Neutral(Background|Stroke|CardBackground)|SubtleBackground)/;
const NEUTRAL_TEXT = /^colorNeutralForeground[1-5](Hover|Pressed|Selected)?$|^colorNeutralForeground(2Link\w*|Disabled)$/;
const NOT_NEUTRAL = /Inverted|OnBrand|Static|Brand|Alpha/;
function warmLight(theme) {
  const out = { ...theme };
  for (const key of Object.keys(out)) {
    const value = out[key];
    if (typeof value !== "string" || NOT_NEUTRAL.test(key)) continue;
    if (!NEUTRAL_SURFACE.test(key) && !NEUTRAL_TEXT.test(key)) continue;
    const v = greyLevel(value);
    if (v === null || v === 255 || v === 0) continue;
    if (NEUTRAL_TEXT.test(key) && value.toLowerCase() === "#242424") out[key] = neutral.ink;
    else if (value.toLowerCase() === "#616161") out[key] = neutral.betonFonce;
    else out[key] = `#${hex(v - 1)}${hex(v - 3)}${hex(v - 7)}`;
  }
  return out;
}
const atelierLight = {
  colorNeutralBackground1Hover: atelier.hover,
  colorNeutralBackground1Pressed: atelier.pressed,
  colorNeutralBackground2: atelier.papier,
  colorNeutralBackground2Hover: atelier.hover,
  colorNeutralBackground2Pressed: atelier.pressed,
  colorNeutralBackground3: neutral.betonClair,
  colorSubtleBackgroundHover: atelier.hover,
  colorSubtleBackgroundPressed: atelier.pressed,
  // Selected items (navigation lists, table rows, toggles) take the blue selection tint.
  colorSubtleBackgroundSelected: atelier.selected,
  colorNeutralBackground1Selected: atelier.selected,
  colorNeutralBackground2Selected: atelier.selected,
  colorNeutralStroke1: atelier.stroke,
  colorNeutralStroke1Hover: atelier.strokeHover,
  colorNeutralStroke2: atelier.divider,
  colorNeutralStroke3: atelier.dividerSubtle
};
const nightGrey = (v) => `#${hex(v * 0.8)}${hex(v * 0.88 + 4)}${hex(v * 0.98 + 14)}`;
function nightDark(theme) {
  const out = { ...theme };
  for (const key of Object.keys(out)) {
    const value = out[key];
    if (typeof value !== "string" || NOT_NEUTRAL.test(key) || !NEUTRAL_SURFACE.test(key)) continue;
    const v = greyLevel(value);
    if (v === null || v < 10 || v > 176) continue;
    out[key] = nightGrey(v);
  }
  return out;
}
const shapes = {
  borderRadiusSmall: "4px",
  borderRadiusMedium: "8px",
  borderRadiusLarge: "12px",
  borderRadiusXLarge: "16px"
};
const type = {
  fontSizeBase100: "11px",
  lineHeightBase100: "16px",
  lineHeightBase300: "22px",
  fontFamilyMonospace: MONO_FONT_STACK
};
const inkShadows = {
  shadow2: "0 1px 2px rgb(11 37 69 / 6%)",
  shadow4: "0 1px 2px rgb(11 37 69 / 6%), 0 1px 1px rgb(11 37 69 / 4%)",
  shadow8: "0 4px 12px rgb(11 37 69 / 8%), 0 1px 2px rgb(11 37 69 / 4%)",
  shadow16: "0 8px 24px rgb(11 37 69 / 12%), 0 2px 4px rgb(11 37 69 / 4%)",
  shadow28: "0 16px 48px rgb(11 37 69 / 18%), 0 4px 8px rgb(11 37 69 / 6%)",
  shadow64: "0 16px 48px rgb(11 37 69 / 18%), 0 4px 8px rgb(11 37 69 / 6%)"
};
const nightShadows = {
  shadow2: "0 1px 2px rgb(0 0 0 / 30%)",
  shadow4: "0 1px 2px rgb(0 0 0 / 30%), 0 1px 1px rgb(0 0 0 / 20%)",
  shadow8: "0 4px 12px rgb(0 0 0 / 35%)",
  shadow16: "0 8px 24px rgb(0 0 0 / 40%)",
  shadow28: "0 16px 48px rgb(0 0 0 / 50%)",
  shadow64: "0 16px 48px rgb(0 0 0 / 50%)"
};
const lightBase = {
  ...warmLight(createLightTheme(brandRamp)),
  ...atelierLight,
  ...shapes,
  ...type,
  ...inkShadows
};
const darkBase = {
  ...nightDark(createDarkTheme(brandRamp)),
  colorSubtleBackgroundSelected: night.selected,
  colorNeutralBackground1Selected: night.selected,
  colorNeutralBackground2Selected: night.selected,
  ...shapes,
  ...type,
  ...nightShadows
};
const darkBrandForeground = {
  colorBrandForeground1: brandRamp[120],
  colorBrandForeground2: brandRamp[130],
  colorBrandForegroundLink: brandRamp[120],
  colorBrandForegroundLinkHover: brandRamp[130],
  colorBrandForegroundLinkPressed: brandRamp[110],
  colorBrandForegroundLinkSelected: brandRamp[120],
  // The brand-tinted neutral text of selected and hovered items (the app bar, navigation panes):
  // Fluent derives it from step 100 (3.2:1 on the dark surfaces); step 130 keeps 5.2:1 on every
  // resting, hover and selected dark surface (REL-QA-03, checked in theme.test.tsx).
  colorNeutralForeground2BrandHover: brandRamp[130],
  colorNeutralForeground2BrandPressed: brandRamp[130],
  colorNeutralForeground2BrandSelected: brandRamp[130]
};
const darkStatusForeground = {
  colorStatusDangerForeground1: darkBase.colorStatusDangerForeground2
};
const lightStatus = statusPairs.light;
const darkStatus = statusPairs.dark;
const lightBrand = {
  bfLogoTile: brandRamp[80],
  bfLogoArch: "#FFFFFF",
  bfLogoFlow: accent.fill,
  bfWordBati: neutral.ink,
  bfWordFlow: brandRamp[80],
  bfFlowLine: accent.fill,
  bfSelectedBackground: atelier.selected,
  bfSelectedBackgroundHover: atelier.selectedHover,
  bfSelectedForeground: brandRamp[80],
  bfTrack: atelier.track,
  bfAccentBackground: accent.tintLight,
  bfChartSeries1: chartSeries.light[0],
  bfChartSeries2: chartSeries.light[1],
  bfStatusSuccessForeground: lightStatus.success.fg,
  bfStatusSuccessBackground: lightStatus.success.bg,
  bfStatusWarningForeground: lightStatus.warning.fg,
  bfStatusWarningBackground: lightStatus.warning.bg,
  bfStatusDangerForeground: lightStatus.danger.fg,
  bfStatusDangerBackground: lightStatus.danger.bg,
  bfStatusInfoForeground: lightStatus.informative.fg,
  bfStatusInfoBackground: lightStatus.informative.bg,
  bfStatusNeutralForeground: lightStatus.neutral.fg,
  bfStatusNeutralBackground: lightStatus.neutral.bg
};
const darkBrand = {
  bfLogoTile: logoOnDark.tile,
  bfLogoArch: "#FFFFFF",
  bfLogoFlow: logoOnDark.flow,
  bfWordBati: "#FFFFFF",
  bfWordFlow: logoOnDark.word,
  bfFlowLine: logoOnDark.flow,
  bfSelectedBackground: night.selected,
  bfSelectedBackgroundHover: night.selectedHover,
  bfSelectedForeground: brandRamp[130],
  bfTrack: darkBase.colorNeutralBackground1Hover,
  bfAccentBackground: accent.tintDark,
  bfChartSeries1: chartSeries.dark[0],
  bfChartSeries2: chartSeries.dark[1],
  bfStatusSuccessForeground: darkStatus.success.fg,
  bfStatusSuccessBackground: darkStatus.success.bg,
  bfStatusWarningForeground: darkStatus.warning.fg,
  bfStatusWarningBackground: darkStatus.warning.bg,
  bfStatusDangerForeground: darkStatus.danger.fg,
  bfStatusDangerBackground: darkStatus.danger.bg,
  bfStatusInfoForeground: darkStatus.informative.fg,
  bfStatusInfoBackground: darkStatus.informative.bg,
  bfStatusNeutralForeground: darkStatus.neutral.fg,
  bfStatusNeutralBackground: darkStatus.neutral.bg
};
const hcText = teamsHighContrastTheme.colorNeutralForeground1;
const hcSurface = teamsHighContrastTheme.colorNeutralBackground1;
const highContrastBrand = {
  bfLogoTile: hcSurface,
  bfLogoArch: hcText,
  bfLogoFlow: hcText,
  bfWordBati: hcText,
  bfWordFlow: hcText,
  bfFlowLine: teamsHighContrastTheme.colorNeutralStroke1,
  bfSelectedBackground: teamsHighContrastTheme.colorNeutralBackground1Selected,
  bfSelectedBackgroundHover: teamsHighContrastTheme.colorNeutralBackground1Hover,
  bfSelectedForeground: teamsHighContrastTheme.colorNeutralForeground1Selected,
  bfTrack: hcSurface,
  bfAccentBackground: hcSurface,
  bfChartSeries1: teamsHighContrastTheme.colorBrandForegroundLink,
  bfChartSeries2: hcText,
  bfStatusSuccessForeground: hcText,
  bfStatusSuccessBackground: hcSurface,
  bfStatusWarningForeground: hcText,
  bfStatusWarningBackground: hcSurface,
  bfStatusDangerForeground: hcText,
  bfStatusDangerBackground: hcSurface,
  bfStatusInfoForeground: hcText,
  bfStatusInfoBackground: hcSurface,
  bfStatusNeutralForeground: hcText,
  bfStatusNeutralBackground: hcSurface
};
const withFont = (theme, dir) => {
  const stack = fontStackFor(dir);
  return {
    ...theme,
    fontFamilyBase: stack,
    fontFamilyNumeric: stack,
    // Arabic script needs a little more line height than Latin.
    ...dir === "rtl" ? { lineHeightBase300: "24px" } : {}
  };
};
const batiFlowLightTheme = withFont({ ...lightBase, ...lightBrand }, "ltr");
const batiFlowDarkTheme = withFont(
  { ...darkBase, ...darkBrandForeground, ...darkStatusForeground, ...darkBrand },
  "ltr"
);
const batiFlowHighContrastTheme = withFont(
  { ...teamsHighContrastTheme, ...shapes, ...type, ...highContrastBrand },
  "ltr"
);
const BASE_THEMES = {
  light: batiFlowLightTheme,
  dark: batiFlowDarkTheme,
  highContrast: batiFlowHighContrastTheme
};
function getTheme(name, dir = "ltr") {
  return dir === "ltr" ? BASE_THEMES[name] : withFont(BASE_THEMES[name], dir);
}
export {
  ARABIC_FONT_STACK,
  LATIN_FONT_STACK,
  MONO_FONT_STACK,
  batiFlowDarkTheme,
  batiFlowHighContrastTheme,
  batiFlowLightTheme,
  brandTokens,
  fontStackFor,
  getTheme
};
//# sourceMappingURL=themes.js.map